import React, { useState } from 'react';
import { apiService } from '../services/apiService';
import { useAuth } from '../context/AuthContext';
import './RazorpayPayment.css';

const RazorpayPayment = () => {
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: 100,
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    productName: '',
    description: ''
  });
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handlePayment = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      alert('Please login first');
      return;
    }
    
    if (loading || processingPayment) {
      console.log('Payment already in progress, ignoring duplicate request');
      return;
    }
    
    setLoading(true);
    setProcessingPayment(false);
    
    try {
      console.log('Creating payment order...');
      const orderResponse = await apiService.createPaymentOrder(paymentData);
      
      if (!orderResponse.success) {
        throw new Error(orderResponse.message || 'Failed to create order');
      }
      
      const orderData = orderResponse.data;
      console.log('Order created:', orderData.razorpayOrderId);
      
      // Create flags to prevent duplicate handling
      let paymentProcessed = false;
      let failureHandled = false;
      
      const options = {
        key: orderData.keyId,
        amount: orderData.amount * 100,
        currency: orderData.currency,
        name: "ClusterMicro Services",
        description: orderData.description,
        order_id: orderData.razorpayOrderId,
        handler: async function (response) {
          if (paymentProcessed || processingPayment) {
            console.log('Payment already processed, ignoring duplicate success callback');
            return;
          }
          paymentProcessed = true;
          setProcessingPayment(true);
          await handlePaymentSuccess(response);
        },
        prefill: {
          name: orderData.customerName,
          email: orderData.customerEmail,
          contact: paymentData.customerPhone
        },
        notes: {
          product_name: orderData.productName
        },
        theme: {
          color: "#DC2626"
        },
        modal: {
          ondismiss: function() {
            console.log('Payment modal dismissed');
            if (!paymentProcessed && !failureHandled) {
              setLoading(false);
              setProcessingPayment(false);
            }
          }
        }
      };
      
      const rzp = new window.Razorpay(options);
      
      rzp.on('payment.failed', async function (response) {
        if (failureHandled || paymentProcessed || processingPayment) {
          console.log('Payment failure already handled, ignoring duplicate failure callback');
          return;
        }
        failureHandled = true;
        setProcessingPayment(true);
        await handlePaymentFailure(response.error);
      });
      
      rzp.open();
      
    } catch (error) {
      console.error('Payment initiation failed:', error);
      alert('Failed to initiate payment: ' + error.message);
      setLoading(false);
      setProcessingPayment(false);
    }
  };
  
  const handlePaymentSuccess = async (response) => {
    try {
      console.log('Payment successful, verifying...');
      const verificationData = {
        razorpayOrderId: response.razorpay_order_id,
        razorpayPaymentId: response.razorpay_payment_id,
        razorpaySignature: response.razorpay_signature
      };
      
      const verificationResponse = await apiService.verifyPayment(verificationData);
      
      if (verificationResponse.success) {
        alert('Payment successful and verified!');
        resetForm();
      } else {
        alert('Payment verification failed: ' + verificationResponse.message);
      }
    } catch (error) {
      console.error('Payment verification failed:', error);
      alert('Payment verification failed: ' + error.message);
    } finally {
      setLoading(false);
      setProcessingPayment(false);
    }
  };
  
  const handlePaymentFailure = async (error) => {
    try {
      console.log('Handling payment failure:', error);
      const failureData = {
        razorpayOrderId: error.metadata?.order_id,
        errorCode: error.code,
        errorDescription: error.description
      };
      
      await apiService.handlePaymentFailure(failureData);
      alert(`Payment failed: ${error.description || 'Unknown error'}`);
    } catch (err) {
      console.error('Error handling payment failure:', err);
      alert('Payment failed and could not record failure details');
    } finally {
      setLoading(false);
      setProcessingPayment(false);
    }
  };
  
  const resetForm = () => {
    setPaymentData({
      amount: 100,
      customerName: '',
      customerEmail: '',
      customerPhone: '',
      productName: '',
      description: ''
    });
  };
  
  return (
    <div className="razorpay-payment-container">
      <h2>Razorpay Payment Gateway</h2>
      
      <form onSubmit={handlePayment} className="payment-form">
        <div className="form-row">
          <div className="form-group">
            <label>Amount (INR):</label>
            <input
              type="number"
              name="amount"
              value={paymentData.amount}
              onChange={handleInputChange}
              min="1"
              required
              disabled={loading || processingPayment}
            />
          </div>
          
          <div className="form-group">
            <label>Customer Name:</label>
            <input
              type="text"
              name="customerName"
              value={paymentData.customerName}
              onChange={handleInputChange}
              required
              disabled={loading || processingPayment}
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label>Email:</label>
            <input
              type="email"
              name="customerEmail"
              value={paymentData.customerEmail}
              onChange={handleInputChange}
              required
              disabled={loading || processingPayment}
            />
          </div>
          
          <div className="form-group">
            <label>Phone:</label>
            <input
              type="tel"
              name="customerPhone"
              value={paymentData.customerPhone}
              onChange={handleInputChange}
              required
              disabled={loading || processingPayment}
            />
          </div>
        </div>
        
        <div className="form-group">
          <label>Product Name:</label>
          <input
            type="text"
            name="productName"
            value={paymentData.productName}
            onChange={handleInputChange}
            required
            disabled={loading || processingPayment}
          />
        </div>
        
        <div className="form-group">
          <label>Description:</label>
          <textarea
            name="description"
            value={paymentData.description}
            onChange={handleInputChange}
            rows="3"
            placeholder="Payment description..."
            disabled={loading || processingPayment}
          />
        </div>
        
        <button 
          type="submit" 
          disabled={loading || processingPayment || !isAuthenticated}
          className="pay-button"
        >
          {loading ? 'Creating Order...' : 
           processingPayment ? 'Processing Payment...' : 
           `Pay ₹${paymentData.amount}`}
        </button>
      </form>
      
      {!isAuthenticated && (
        <div className="auth-warning">
          Please login to make payments
        </div>
      )}
      
      {processingPayment && (
        <div className="processing-info">
          Payment is being processed. Please wait...
        </div>
      )}
    </div>
  );
};

export default RazorpayPayment;